<template>
  <div class="text-center pb-0 px-4 sm:px-6 lg:px-8">
    <div class="mb-6 flex flex-row-reverse">
      <SearchInput v-model="keyword" @input="fetchUsersOnSearch" />
    </div>
    <div class="overflow-x-auto rounded-lg bg-white shadow">
      <div class="align-middle inline-block w-full">
        <table class="divide-gray-300 w-full text-left table-auto">
          <thead class="border-b-2">
            <tr>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <FilterableHeaderCell
                  title="Email"
                  param="email"
                  @switchOrder="onSwitchOrder"
                />
              </th>
              <th
                scope="col"
                class="px-6 py-5 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                <span class="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody v-if="users.length > 0">
            <tr v-for="user in users" :key="user.id">
              <td
                class="py-5 px-6 text-sm text-gray-500 hover:bg-gray-50 cursor-pointer"
                @click="goToUser(user.id)"
              >
                {{ user.email }}
                <span
                  v-if="user.is_parent"
                  class="px-2 text-blue-800 bg-blue-100 rounded-full border-gray-200"
                  >Parent user</span
                >
              </td>
              <td class="py-5 px-6">
                <button
                  v-if="!user.is_parent && user.is_active"
                  type="button"
                  class="button-submit w-40"
                  @click.stop="transferParent(user)"
                >
                  Make Parent User
                </button>
              </td>
            </tr>
          </tbody>
          <tbody v-else>
            <tr>
              <td class="py-5 px-6 text-sm text-gray-500" colspan="2">
                No data
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
<script>
import { ref } from "vue";
import FilterableHeaderCell from "@/components/common/FilterableHeaderCell";
import SearchInput from "@/components/common/SearchInput";
import useConfirmationModal from "@/api/confirmationModal";
import eventBus from "@/utils/eventBus";

const HTML_ESCAPES = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&#39;",
};

function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>"']/g, (char) => HTML_ESCAPES[char]);
}

export default {
  name: "LinkedAccounts",
  components: {
    FilterableHeaderCell,
    SearchInput,
  },
  props: {
    userId: Number,
    default: null,
  },
  async beforeMount() {
    await this.fetchUsers();
  },
  setup() {
    return {
      users: ref([]),
      keyword: ref(""),
    };
  },
  methods: {
    async fetchUsers() {
      const users = await this.$alfredService.linkedAccountList(
        this.userId,
        this.keyword
      );
      const { results } = users;
      this.users = results;
    },
    async fetchUsersOnSearch() {
      await this.fetchUsers();
    },
    async goToUser(user_id) {
      await this.$router.push({
        name: "UserDetails",
        params: { userId: user_id },
      });
      this.$emit("goToUser", user_id);
    },
    async transferParent(user) {
      // ConfirmationModal renders `message` with v-html, so escape the
      // (user-controlled) email before interpolating it in.
      const safeEmail = escapeHtml(user.email);
      const modal = useConfirmationModal();
      await modal.openModal({
        title: "Are you sure you want to confirm?",
        message: `${safeEmail} will become the Parent user for this organization and the current parent will be demoted. Any personal credit balance on ${safeEmail}'s account will be merged into the organization's shared pool.`,
        primaryButton: "Make Parent User",
        onSubmit: async () => {
          try {
            await this.$alfredService.adminTransferParent(user);
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "success",
                text: "Parent user updated successfully!",
              },
            });
          } catch (error) {
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "error",
                text:
                  error?.response?.data?.detail ||
                  "Failed to update the Parent user. Please try again.",
              },
            });
            return;
          }

          try {
            await this.fetchUsers();
          } catch (error) {
            eventBus.trigger("notify", {
              notification: {
                group: "top-right",
                type: "error",
                text: "Parent user updated, but the list failed to refresh. Please reload.",
              },
            });
          }
        },
      });
    },
  },
};
</script>
